/**
 * azure-functions/shared/workflows/fetchSources.cjs
 *
 * The Fetch data sources a workflow can actually read (PDWA-04,
 * docs/workflow-engine-design.md §3a "Fetch data is High only when the read is
 * fresh", §3i). One copy, shared by the browser and the server: the validator
 * refuses a definition whose Fetch step names anything else (so a run can
 * never wait on a source nothing writes), and the runner reads through
 * `readFetchSource` here.
 *
 * Every entry is backed by a working connector already in the repo — an
 * integration sync that writes a tenant state key and stamps when it ran:
 *
 *   entra-security-posture       entra-security-posture → state.securityPosture
 *                                (tenant_integrations 'entra-id')
 *   defender-cloud-secure-score  ms-cloud-posture-sync → state.msCloudPosture
 *                                (tenant_integrations 'microsoft-cloud',
 *                                'defender-cloud' or 'entra-id')
 *
 * These are the same feeds, connection rows and freshness rule the
 * measurement registry reads (shared/measurement/measures/volatile.cjs,
 * INTEGRATION_MAX_AGE_HOURS = 72): a source that is disconnected, never
 * synced or older than 72 h is `not-obtained` with no evidence, never a stale
 * value presented as a High read (TRYGGR_INTENT.md §5).
 *
 * Adding a source is a design change: it joins here only when its connector
 * exists and writes the state key named. There is no stub source.
 */

'use strict';

/** How old a source's data may be when a run reads it (the measurement rule). */
const FETCH_MAX_AGE_HOURS = 72;
const HOUR_MS = 60 * 60 * 1000;
const CLOCK_SKEW_MS = 5 * 60 * 1000;

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const intOrNull = (v) => (Number.isInteger(v) && v >= 0 ? v : null);

function mfaBlock(posture) {
  const mfa = isObj(posture) && isObj(posture.mfa) ? posture.mfa : null;
  // A failed MFA check zeroes both counts in the sync — never read that as 0.
  if (!mfa || mfa.error) return null;
  return mfa;
}

const FETCH_SOURCES = Object.freeze({
  'entra-security-posture': Object.freeze({
    id: 'entra-security-posture',
    label: 'Microsoft Entra ID — security posture',
    integrationIds: Object.freeze(['entra-id']),
    stateKey: 'securityPosture',
    timeField: 'lastRefreshed',
    fields: Object.freeze({
      mfaEnabledCount: Object.freeze({
        label: 'Users with MFA registered',
        read: (p) => {
          const mfa = mfaBlock(p);
          return mfa ? intOrNull(mfa.enabledCount) : null;
        },
      }),
      mfaTotalUsers: Object.freeze({
        label: 'Users checked for MFA',
        read: (p) => {
          const mfa = mfaBlock(p);
          return mfa ? intOrNull(mfa.totalUsers) : null;
        },
      }),
      staleAccountCount: Object.freeze({
        label: 'Stale accounts',
        read: (p) => (isObj(p) && isObj(p.staleAccounts) ? intOrNull(p.staleAccounts.count) : null),
      }),
    }),
  }),
  'defender-cloud-secure-score': Object.freeze({
    id: 'defender-cloud-secure-score',
    label: 'Microsoft Defender for Cloud — secure score',
    integrationIds: Object.freeze(['microsoft-cloud', 'defender-cloud', 'entra-id']),
    stateKey: 'msCloudPosture',
    timeField: 'syncedAt',
    fields: Object.freeze({
      secureScorePercent: Object.freeze({
        label: 'Secure score (mean of subscriptions, %)',
        read: (cloud) => {
          const subs = isObj(cloud) && Array.isArray(cloud.subscriptions) ? cloud.subscriptions : [];
          const scores = subs
            .map((s) => s && s.secureScore && s.secureScore.percentage)
            .filter((v) => typeof v === 'number' && Number.isFinite(v));
          if (scores.length === 0) return null;
          return Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 100) / 100;
        },
      }),
    }),
  }),
});

const FETCH_SOURCE_IDS = Object.freeze(Object.keys(FETCH_SOURCES));

function isFetchSource(id) {
  return typeof id === 'string' && Object.prototype.hasOwnProperty.call(FETCH_SOURCES, id);
}

function isFetchField(sourceId, field) {
  return isFetchSource(sourceId) && Object.prototype.hasOwnProperty.call(FETCH_SOURCES[sourceId].fields, field);
}

function toMs(v) {
  if (v == null || v === '') return NaN;
  const t = v instanceof Date ? v.getTime() : Date.parse(v);
  return Number.isFinite(t) ? t : NaN;
}

/**
 * Read one source for a run, from the tenant state a connector wrote and the
 * tenant's integration rows. Pure.
 *
 * @param {string} sourceId
 * @param {{ state: object, integrations: Array<{ integrationId, enabled }>, now: Date|string }} input
 * @returns {{ outcome: 'obtained'|'not-obtained', reason?: string, sourceAsOf: string|null,
 *   values: Object<string, number|null> }}
 */
function readFetchSource(sourceId, { state, integrations, now }) {
  const src = FETCH_SOURCES[sourceId];
  const notObtained = (reason, sourceAsOf = null) => ({ outcome: 'not-obtained', reason, sourceAsOf, values: {} });
  if (!src) return notObtained('unknown source');
  const rows = (Array.isArray(integrations) ? integrations : []).filter(
    (r) => r && src.integrationIds.includes(r.integrationId) && r.enabled
  );
  if (rows.length === 0) return notObtained('integration disconnected');
  const data = isObj(state) && isObj(state[src.stateKey]) ? state[src.stateKey] : null;
  const at = data ? toMs(data[src.timeField]) : NaN;
  if (Number.isNaN(at)) return notObtained('never synced');
  const sourceAsOf = new Date(at).toISOString();
  if (toMs(now) - at > FETCH_MAX_AGE_HOURS * HOUR_MS) {
    return notObtained(`older than ${FETCH_MAX_AGE_HOURS} h`, sourceAsOf);
  }
  // A time in the future is not a fresh read — it is an unreliable one.
  if (at - toMs(now) > CLOCK_SKEW_MS) return notObtained('stamped in the future', sourceAsOf);
  const values = {};
  for (const [key, f] of Object.entries(src.fields)) values[key] = f.read(data);
  if (Object.values(values).every((v) => v === null)) return notObtained('no readable values', sourceAsOf);
  return { outcome: 'obtained', sourceAsOf, values };
}

module.exports = {
  FETCH_SOURCES,
  FETCH_SOURCE_IDS,
  FETCH_MAX_AGE_HOURS,
  isFetchSource,
  isFetchField,
  readFetchSource,
};
